import { cloneDeep } from 'lodash';
import { aggregateGroupValues, createGroupHeader, getPickupsInGroup } from '../GroupPickup/utils';
import { getDriverStopDetails } from '../../ProDetails/PickupProInfo/utils';

type PickupRecord = Record<string, any>;

/** Toggle / flag fields shown in pickup info: OR across all pickups in the group. */
const TOGGLE_OR_FIELDS = [
  'isExhibitPickup',
  'isSwapPickup',
  'isDockDropPickup',
  'isLiftGate',
  'isWarehousePickup',
  'isDailyPickup',
  'isShipperLoadAndCount',
  'isDriverAllowedInside'
] as const;

export const applyBooleanOrAcrossMembers = (basePickupInfo: PickupRecord, members?: PickupRecord[]) => {
  const next: PickupRecord = { ...basePickupInfo };
  const list = members?.length ? members : [basePickupInfo];
  TOGGLE_OR_FIELDS.forEach((field) => {
    next[field as string] = list.some((p) => !!p[field as string]);
  });
  return next;
};

/**
 * React Router history state is serialized (structured clone). This strips functions,
 * class instances, MobX observables/proxies, `undefined` values, etc., so navigation
 * does not throw and downstream code receives plain JSON-compatible objects.
 */
export const cloneNavigateStateForHistory = (state: PickupRecord): PickupRecord =>
  JSON.parse(JSON.stringify(state));

export type GroupPickupDialogBuiltState = {
  pickupInfo: PickupRecord;
  groupMemberPickups: PickupRecord[];
  groupBulkEditPickupIds: string[];
  driverDetails: PickupRecord | null;
};

/**
 * Builds pickup info + member list for the group pickup dialog from the dispatch pickups grid list.
 */
export const buildGroupPickupDialogState = (
  groupId: string | null | undefined,
  pickupsList: PickupRecord[] | null | undefined,
  currentActiveRoutes: unknown
): GroupPickupDialogBuiltState | null => {
  if (!groupId || !pickupsList?.length) {
    return null;
  }
  const members = getPickupsInGroup(groupId, pickupsList);
  if (!members.length) {
    return null;
  }
  const primary = members.find((p: PickupRecord) => p.isPrimary) || members[0];
  const aggregatedValues = aggregateGroupValues(members);
  const groupHeader = createGroupHeader(groupId, members, primary, aggregatedValues);

  let pickupInfo: PickupRecord = cloneDeep(primary);
  Object.assign(pickupInfo, {
    ...groupHeader,
    pickupId: primary.pickupId,
    pickupNumber: primary.pickupNumber,
    specialInstructions: groupHeader.allSpecialInstructions || groupHeader.specialInstructions || '',
    allSpecialInstructions: groupHeader.allSpecialInstructions || groupHeader.specialInstructions || ''
  });
  if (groupHeader.groupAggregatedData) {
    Object.assign(pickupInfo, groupHeader.groupAggregatedData);
  }
  pickupInfo = applyBooleanOrAcrossMembers(pickupInfo, members);

  const groupMemberPickups = cloneDeep(members);
  const groupBulkEditPickupIds = members
    .map((p: PickupRecord) => String(p.pickupId || '').toUpperCase())
    .filter(Boolean);

  const driverDetails = cloneDeep(
    getDriverStopDetails({
      activeDriverDetails: {
        currentActiveRoutes,
        pickupInfo: { ...pickupInfo, pickupId: primary.pickupId }
      },
      historicDriverDetails: null
    })
  );

  return {
    pickupInfo,
    groupMemberPickups,
    groupBulkEditPickupIds,
    driverDetails
  };
};
